import React, { memo, useMemo } from '../../lib/teact/teact';
import { getActions, getGlobal } from '../../global';

import type { ApiChain } from '../../api/types';
import { MediaType, type NftTransfer } from '../../global/types';

import { ANIMATED_STICKER_TINY_SIZE_PX, DEFAULT_CHAIN } from '../../config';
import buildClassName from '../../util/buildClassName';
import { openUrl } from '../../util/openUrl';
import { shortenAddress } from '../../util/shortenAddress';
import { getExplorerName, getExplorerNftUrl } from '../../util/url';
import { ANIMATED_STICKERS_PATHS } from '../ui/helpers/animatedAssets';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import AnimatedIconWithPreview from '../ui/AnimatedIconWithPreview';

import styles from './NftInfo.module.scss';

interface OwnProps {
  nft?: NftTransfer;
  chain?: ApiChain;
  withTonExplorer?: boolean;
  // Currently, MediaViewer can only display NFTs that are owned by the user
  withMediaViewer?: boolean;
}

function NftInfo({
  nft, withTonExplorer, withMediaViewer, chain = DEFAULT_CHAIN,
}: OwnProps) {
  const { openMediaViewer } = getActions();
  const lang = useLang();
  const { isTestnet, selectedExplorerIds } = getGlobal().settings;
  const explorerId = selectedExplorerIds?.[chain];
  const explorerUrl = getExplorerNftUrl(chain, nft?.address, isTestnet, explorerId);

  const tonExplorerTitle = useMemo(() => {
    return (lang('Open on %explorer_name%', {
      explorer_name: getExplorerName(chain, explorerId),
    }) as string[]
    ).join('');
  }, [lang, chain, explorerId]);

  const handleClickInfo = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (explorerUrl) void openUrl(explorerUrl);
  };

  const handleClick = useLastCallback(() => {
    openMediaViewer({ mediaId: nft!.address, mediaType: MediaType.Nft });
  });

  if (!nft) {
    return (
      <div className={styles.root}>
        <AnimatedIconWithPreview
          play
          noLoop={false}
          nonInteractive
          size={ANIMATED_STICKER_TINY_SIZE_PX}
          className={styles.thumbnail}
          tgsUrl={ANIMATED_STICKERS_PATHS.wait}
          previewUrl={ANIMATED_STICKERS_PATHS.waitPreview}
        />

        <div className={styles.info}>
          <div className={styles.title}>NFT</div>
          <div className={styles.description}>
            {lang('Name and collection are not available. Please check back later.')}
          </div>
        </div>
      </div>
    );
  }

  const name = nft.name || shortenAddress(nft.address);
  const { thumbnail } = nft;

  function renderContent() {
    return (
      <>
        {thumbnail && <img src={thumbnail} alt={name} className={styles.thumbnail} />}

        <div className={styles.info}>
          <div className={styles.title}>
            {name}
            {
              withTonExplorer && explorerUrl && (
                <i
                  className={buildClassName(styles.icon, 'icon-explorer-small')}
                  onClick={handleClickInfo}
                  title={tonExplorerTitle}
                  aria-label={tonExplorerTitle}
                  role="button"
                  tabIndex={0}
                />
              )
            }
          </div>
          <div className={styles.description}>{nft!.collectionName}</div>
        </div>
      </>
    );
  }

  if (!withMediaViewer) {
    return (
      <div className={styles.root}>
        {renderContent()}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={lang('NFT')}
      className={buildClassName(styles.root, styles.interactive)}
      onClick={handleClick}
    >
      {renderContent()}
    </div>
  );
}

export default memo(NftInfo);
